/**
 * The engine's pitch and loudness from road speed, with gear changes. Pure numbers (no Web Audio) so it
 * can be tested. Speeds are m/s; pitch is the engine's base frequency in Hz.
 */
export const GEAR_TOPS = [7, 14, 23, 34, 48, Infinity] as const;

export interface EngineVoice {
  /** Base pitch at idle and at the top of each gear, Hz. */
  idleHz: number;
  topHz: number;
  /** How bright the sound is (lowpass cutoff), Hz at idle and flat out. */
  brightness: [number, number];
}

/** Diesel trotros rumble low; the Vitz is a small petrol engine, higher and buzzier. */
export const ENGINE_VOICES: Record<string, EngineVoice> = {
  sprinter: { idleHz: 30, topHz: 88, brightness: [220, 700] },
  hiace: { idleHz: 34, topHz: 98, brightness: [250, 800] },
  vitz: { idleHz: 44, topHz: 135, brightness: [340, 1150] },
};

export interface EngineState {
  hz: number;
  /** 0..1: how hard the engine is working (louder and brighter when accelerating). */
  load: number;
  gear: number;
}

/** Engine note for a speed. `accelerating` (speed rising) adds load; between gears the pitch drops back. */
export function engineAt(speed: number, accelerating: boolean, voice: EngineVoice): EngineState {
  const v = Math.max(0, speed);
  let gear = 0;
  while (gear < GEAR_TOPS.length - 1 && v > GEAR_TOPS[gear]!) gear += 1;
  const bottom = gear === 0 ? 0 : GEAR_TOPS[gear - 1]!;
  const top = Number.isFinite(GEAR_TOPS[gear]) ? GEAR_TOPS[gear]! : bottom + 40;
  // Each gear starts around 40% revs and climbs to the top.
  const revs = v < 0.5 ? 0 : 0.4 + 0.6 * Math.min(1, (v - bottom) / (top - bottom));
  const hz = voice.idleHz + (voice.topHz - voice.idleHz) * revs;
  const load = v < 0.5 ? 0.15 : Math.min(1, (accelerating ? 0.75 : 0.35) + revs * 0.25);
  return { hz, load, gear };
}
